import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { splitAddressDisplay } from '@/lib/address'
import { searchPropertiesByPrefix, type PropertySuggestion } from '@/lib/callables'
import { openPropertyProfileOnMap } from '@/lib/property-navigation'
import { cn } from '@/lib/utils'

/** Classic Firestore prefix search — kept for rollback via search_backend_v2=false. */
export function ClassicPropertySearchBox({
  autoFocus,
  className,
  placeholder = 'Search any Australian address…',
}: {
  autoFocus?: boolean
  className?: string
  placeholder?: string
}) {
  const navigate = useNavigate()
  const rootRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<PropertySuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [focused, setFocused] = useState(false)

  useEffect(() => {
    const q = query.trim()
    if (q.length < 3) {
      setResults([])
      setLoading(false)
      return
    }
    let cancelled = false
    const timer = window.setTimeout(async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await searchPropertiesByPrefix(q)
        if (!cancelled) setResults(data.properties ?? [])
      } catch (e) {
        if (!cancelled) {
          setResults([])
          setError(e instanceof Error ? e.message : 'Search failed')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [query])

  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      const root = rootRef.current
      if (!root) return
      if (e.target instanceof Node && !root.contains(e.target)) {
        setFocused(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [])

  const showSuggestions = focused && results.length > 0

  return (
    <div ref={rootRef} className={cn('relative w-full', className)}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
        <Input
          autoFocus={autoFocus}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          placeholder={placeholder}
          className="h-12 pl-10 pr-10 text-base focus-visible:ring-1 focus-visible:ring-biddle-blue"
          aria-label="Search properties by address"
          aria-expanded={showSuggestions}
        />
        {loading && focused ? (
          <Spinner className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2" />
        ) : null}
      </div>
      {error && focused ? <p className="mt-2 text-sm text-danger">{error}</p> : null}
      {showSuggestions ? (
        <ul className="absolute z-30 mt-2 max-h-80 w-full overflow-auto rounded-2xl border border-border bg-white py-1 shadow-xl">
          {results.map((item) => {
            const { street, locality } = splitAddressDisplay(item.fullAddress)
            return (
              <li key={item.id}>
                <button
                  type="button"
                  className="flex w-full flex-col items-start px-4 py-3 text-left hover:bg-surface"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setQuery('')
                    setResults([])
                    setFocused(false)
                    openPropertyProfileOnMap(navigate, item.id)
                  }}
                >
                  <span className="text-sm font-medium text-navy">{street}</span>
                  {locality ? <span className="text-xs text-muted">{locality}</span> : null}
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
